import React, {useEffect, useMemo, useState} from 'react';
import './SurveyResponseModal.scss';
import i18next from "i18next";
import parse from 'html-react-parser';
import {Translation} from "react-i18next";
import GeneralModal from "../GeneralModal/GeneralModal";
import LabeledDropDown from "../../Custom UI/LabeledInputs/LabeledDropDown/LabeledDropDown";
import LabeledSwitch from "../../Custom UI/LabeledInputs/LabeledSwitch/LabeledSwitch";
import PaperTrailVersionsList from "../../Lists/PaperTrailVersionsList/PaperTrailVersionsList";
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import Separator from "../../Custom UI/Separator/Separator";
import {momentFromDateTime} from "../../Helpers/DateHelpers";
import {QuestionCells} from "../../Components/SurveyComponent/SurveyComponent";
import RedButton from "../../Custom UI/Buttons/RedButton/RedButton";
import BlueButton from "../../Custom UI/Buttons/BlueButton/BlueButton";
import AddDataQueryModal from "../AddDataQueryModal/AddDataQueryModal";
import TabBar from "../../Custom UI/TabBar/TabBar";
import DataQueriesList from "../../Lists/DataQueriesList/DataQueriesList";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import {roleForCompany} from "../../Helpers/CompanyHelpers";
import {toast} from "react-toastify";
import {NOTIFY_OPTS} from "../../constants/Notifiers";
import {errorMessageFromServerError} from "../../Helpers/Helpers";

const notifySurveyResponseDeleteOrRestoreSuccess = (aIsRestore, aIsDetail) => toast(i18next.t(aIsRestore ? (aIsDetail ? 'RESTORE_SURVEY_RESPONSE_DETAIL_SUCCESS' : 'RESTORE_SURVEY_RESPONSE_SUCCESS') : (aIsDetail ? 'DELETE_SURVEY_RESPONSE_DETAIL_SUCCESS' : 'DELETE_SURVEY_RESPONSE_SUCCESS')), NOTIFY_OPTS.autoClose);
const notifySurveyResponseDeleteOrRestoreError = (aIsRestore, aIsDetail, aError) => toast(i18next.t(aIsRestore ? (aIsDetail ? 'RESTORE_SURVEY_RESPONSE_DETAIL_ERROR' : 'RESTORE_SURVEY_RESPONSE_ERROR') : (aIsDetail ? 'DELETE_SURVEY_RESPONSE_DETAIL_ERROR' : 'DELETE_SURVEY_RESPONSE_ERROR'), {aError}), NOTIFY_OPTS.autoClose);

const answerFromResponseDetail = (aResponseDetail, aQuestion) => {
  if(!aResponseDetail){
    return null;
  }

  const values = Array.isArray(aResponseDetail.values) ? aResponseDetail.values : [];
  const prettyValues = values.map((aValue) => {
    if(aQuestion && aQuestion.type === 'checkbox'){
      return {[aValue.key]:aValue.value === 'true'};
    }
    return {[aValue.key]:aValue.value};
  });

  return {
    value:aResponseDetail.value,
    values:values,
    prettyValues:prettyValues,
    other:aResponseDetail.other,
    other_value:aResponseDetail.other_value,
    skipped:aResponseDetail.skipped,
    skip:aResponseDetail.skip,
    created_at:aResponseDetail.created_at,
    locale:aResponseDetail.locale,
    ai_response:aResponseDetail.ai_response,
    ai_response_approved:aResponseDetail.ai_response_approved,
    audio_transcription:aResponseDetail.audio_transcription
  };
};

const SurveyResponseModal = ({
  open,
  close,
  company,
  surveyResponseId,
  userName,
  title,
  lockedState,
  surveyResponse,
  surveyReference,
  fetchQuestionsForSurveyByReference,
  getSurveyResponseDetails,
  getAdminHistories,
  addDataQuery,
  deleteSurveyResponse,
  restoreSurveyResponse,
  deleteSurveyResponseDetail,
  restoreSurveyResponseDetail,
  reloadSurveyResponses,
  patient,
  user,
  createDataQueryNote,
  getDataQueryNotes,
  getDataQueryNoteComments,
  resolveDataQueryNote,
  createDataQueryComment,
  showPatientSearch = false,
  initialFromDate,
  initialToDate
}) => {
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [responseQuestion, setResponseQuestion] = useState(null);
  const [includeDeleted, setIncludeDeleted] = useState(['admin', 'admin_level_3', 'admin_level_4'].includes(roleForCompany(company)));
  const [responseDetails, setResponseDetails] = useState([]);
  const [selectedResponseDetail, setSelectedResponseDetail] = useState(null);
  const [responseDetailLoading, setResponseDetailLoading] = useState(false);
  const [questionLoadError, setQuestionLoadError] = useState('');
  const [responseDeletedAt, setResponseDeletedAt] = useState(null);
  const [showFullQuestionLabel, setShowFullQuestionLabel] = useState(false);
  const [dataQueryModalOpen, setDataQueryModalOpen] = useState(false);
  const [rightTabKey, setRightTabKey] = useState('HISTORY');
  const [dataQueryListKey, setDataQueryListKey] = useState(0);
  const [deleteRestoreConfirmationOpen, setDeleteRestoreConfirmationOpen] = useState(false);
  const [responseDetailsReloadKey, setResponseDetailsReloadKey] = useState(0);
  const [saving, setSaving] = useState(false);

  const modalTitle = [userName, title, lockedState].filter((aValue) => aValue !== null && aValue !== undefined && `${aValue}`.length > 0).join(' - ');
  const resolvedSurveyReference = surveyReference || (surveyResponse && surveyResponse.survey && surveyResponse.survey.reference) || null;
  const surveyResponseDeletedAt = surveyResponse && surveyResponse.deleted_at;
  const selectedObjectIsDeleted = selectedResponseDetail
    ? selectedResponseDetail.deleted_at !== null && selectedResponseDetail.deleted_at !== undefined
    : responseDeletedAt !== null && responseDeletedAt !== undefined;
  const resolvedInitialFromDate = selectedResponseDetail && selectedResponseDetail.created_at
    ? selectedResponseDetail.created_at
    : surveyResponse && surveyResponse.created_at ? surveyResponse.created_at : undefined;

  useEffect(() => {
    if(!open){
      return;
    }

    setSelectedQuestion(null);
    setResponseQuestion(null);
    setResponseDetails([]);
    setSelectedResponseDetail(null);
    setIncludeDeleted(['admin', 'admin_level_3', 'admin_level_4'].includes(roleForCompany(company)));
    setResponseDeletedAt(surveyResponseDeletedAt || null);
    setShowFullQuestionLabel(false);
    setSaving(false);
  }, [open, surveyResponseId, surveyResponseDeletedAt, company]);

  const surveyResponseDeleteOrRestore = () => {
    const isDeleted = responseDeletedAt !== null;
    const responseAction = isDeleted ? restoreSurveyResponse : deleteSurveyResponse;
    const responseId = surveyResponse && surveyResponse.id ? surveyResponse.id : surveyResponseId;

    if(responseAction && responseId && company && company.id){
      setSaving(true);
      responseAction(responseId, company.id, surveyResponse && surveyResponse.is_test_response).then(() => {
        notifySurveyResponseDeleteOrRestoreSuccess(isDeleted, false);
        setResponseDeletedAt(isDeleted ? null : new Date().toISOString());
        setResponseDetailsReloadKey((aKey) => aKey + 1);
        setDataQueryListKey((aKey) => aKey + 1);
        setSaving(false);
        if(reloadSurveyResponses){
          reloadSurveyResponses();
        }
        setDeleteRestoreConfirmationOpen(false);
      }, (newError) => {
        setSaving(false);
        notifySurveyResponseDeleteOrRestoreError(isDeleted, false, errorMessageFromServerError(newError));
      });
    }
  };

  const surveyResponseDetailDeleteOrRestore = () => {
    if(selectedResponseDetail && selectedResponseDetail.id && company && company.id){
      const isDeleted = selectedResponseDetail.deleted_at !== null && selectedResponseDetail.deleted_at !== undefined;
      const responseDetailAction = isDeleted ? restoreSurveyResponseDetail : deleteSurveyResponseDetail;

      if(responseDetailAction){
        setSaving(true);
        responseDetailAction(selectedResponseDetail.id, company.id).then(() => {
          notifySurveyResponseDeleteOrRestoreSuccess(isDeleted, true);
          setSelectedResponseDetail({...selectedResponseDetail, deleted_at:isDeleted ? null : new Date().toISOString()});
          setResponseDetailsReloadKey((aKey) => aKey + 1);
          setDataQueryListKey((aKey) => aKey + 1);
          setSaving(false);
          setDeleteRestoreConfirmationOpen(false);
        }, (newError) => {
          setSaving(false);
          notifySurveyResponseDeleteOrRestoreError(isDeleted, true, errorMessageFromServerError(newError));
        });
      }
    }
  };

  const openDataQueryModal = () => {
    setDataQueryModalOpen(true);
  };

  const fetchQuestionObjects = (aLimit, aOffset, aSearchString) => {
    let questionsPromise = Promise.resolve({
      data:{
        data:{
          survey_questions_for_survey_version_collection:[]
        }
      }
    });

    if(open && resolvedSurveyReference && fetchQuestionsForSurveyByReference){
      questionsPromise = fetchQuestionsForSurveyByReference(resolvedSurveyReference).then((newResponse) => {
        const questions = newResponse && newResponse.data && newResponse.data.data && newResponse.data.data.survey_questions_for_survey_version_collection
          ? newResponse.data.data.survey_questions_for_survey_version_collection
          : [];
        const normalizedQuestions = questions.map((question) => ({...question, id:question.key}));
        const normalizedSearchString = aSearchString && aSearchString.length > 0 ? aSearchString.toLowerCase() : '';
        const filteredQuestions = normalizedSearchString.length > 0
          ? normalizedQuestions.filter((question) => {
            const label = `${question.label || question.unparsed_label || question.key || ''}`.toLowerCase();
            return label.includes(normalizedSearchString) || `${question.key || ''}`.toLowerCase().includes(normalizedSearchString);
          })
          : normalizedQuestions;

        return {
          data:{
            data:{
              survey_questions_for_survey_version_collection:filteredQuestions
            }
          }
        };
      });
    }

    return questionsPromise;
  };

  const questionOptionsFormatter = (aQuestionsArray) => {
    return (Array.isArray(aQuestionsArray) ? aQuestionsArray : []).map((question) => {
      return {
        ...question,
        id:question.key,
        label:question.label && question.label.length > 0 ? question.label : question.unparsed_label || question.key,
        value:question.key
      };
    });
  };

  useEffect(() => {
    let isActive = true;

    if(!open || !selectedQuestion || !surveyResponseId || !getSurveyResponseDetails){
      setResponseQuestion(null);
      setResponseDetails([]);
      setSelectedResponseDetail(null);
      return () => {
        isActive = false;
      };
    }

    setResponseDetailLoading(true);
    const canIncludeDeleted = ['admin', 'admin_level_3', 'admin_level_4'].includes(roleForCompany(company));
    getSurveyResponseDetails(surveyResponseId, selectedQuestion.key, canIncludeDeleted && includeDeleted).then((newResponse) => {
      if(!isActive){
        return;
      }
      const responseDetail = newResponse && newResponse.data && newResponse.data.data && newResponse.data.data.getSurveyResponseDetail
        ? newResponse.data.data.getSurveyResponseDetail
        : null;
      const responseDetailArray = responseDetail && Array.isArray(responseDetail.array) ? responseDetail.array : [];

      setResponseQuestion(responseDetail && responseDetail.question ? responseDetail.question : null);
      setResponseDetails(responseDetailArray);
      setSelectedResponseDetail((previousResponseDetail) => {
        if(!previousResponseDetail){
          return null;
        }
        return responseDetailArray.find((aResponseDetail) => `${aResponseDetail.id}` === `${previousResponseDetail.id}`) || null;
      });
      setResponseDetailLoading(false);
    }, () => {
      if(!isActive){
        return;
      }
      setResponseQuestion(null);
      setResponseDetails([]);
      setSelectedResponseDetail(null);
      setResponseDetailLoading(false);
    });

    return () => {
      isActive = false;
    };
  }, [open, selectedQuestion, surveyResponseId, getSurveyResponseDetails, includeDeleted, responseDetailsReloadKey, company]);

  const selectedQuestionWithAnswer = useMemo(() => {
    if(!selectedQuestion || !selectedResponseDetail){
      return null;
    }

    return {
      ...(responseQuestion || selectedQuestion),
      key:(responseQuestion && responseQuestion.key) || selectedQuestion.key,
      value:selectedQuestion.value,
      surveyResponseDetailId:selectedResponseDetail ? selectedResponseDetail.id : null
    };
  }, [selectedQuestion, responseQuestion, selectedResponseDetail]);

  const surveyResponseForMeta = surveyResponse || null;

  const handleQuestionChange = (aOption) => {
    setQuestionLoadError('');
    const questionKey = aOption && (aOption.value || aOption.key);
    setResponseQuestion(null);
    setResponseDetails([]);
    setSelectedResponseDetail(null);
    setShowFullQuestionLabel(false);
    setSelectedQuestion(questionKey ? {
      ...aOption,
      label:aOption.label || aOption.unparsed_label || questionKey,
      value:questionKey,
      key:aOption.key || questionKey
    } : null);
  };

  const responseDetailOptions = responseDetails.map((aResponseDetail, aIndex) => ({
    label:`${aResponseDetail.id ? aResponseDetail.id : aIndex + 1}${aResponseDetail.deleted_at ? ` - ${i18next.t('DELETED')}` : ''}`,
    value:aResponseDetail.id,
    responseDetail:aResponseDetail
  }));

  const handleResponseDetailChange = (aOption) => {
    const responseDetail = aOption && aOption.value
      ? responseDetails.find((aResponseDetail) => `${aResponseDetail.id}` === `${aOption.value}`)
      : null;
    setSelectedResponseDetail(responseDetail || null);
    if(!responseDetail){
      setRightTabKey('HISTORY');
    }
  };

  const notifyQuestionFetchFailed = (aError) => {
    setQuestionLoadError(aError || i18next.t('ERROR'));
  };

  const renderQuestionCell = () => {
    if(!selectedQuestionWithAnswer){
      return null;
    }

    if(!selectedQuestionWithAnswer.type || selectedQuestionWithAnswer.type === 'blank'){
      return null;
    }

    if(selectedQuestionWithAnswer.type === 'survey' && !QuestionCells.survey){
      return (
        <div className="survey-response-modal-placeholder">
          <Translation>{(t, {i18n}) => t('SURVEY_RESPONSE_DETAIL_UNAVAILABLE')}</Translation>
        </div>
      );
    }

    const QuestionCell = QuestionCells[selectedQuestionWithAnswer.type];

    if(!QuestionCell){
      return (
        <div className="survey-response-modal-placeholder">
          <Translation>{(t, {i18n}) => t('ERROR_UNKNOWN_SURVEY_QUESTION', {aQuestionType:selectedQuestionWithAnswer.type})}</Translation>
        </div>
      );
    }

    const responseDetailAnswer = answerFromResponseDetail(selectedResponseDetail, selectedQuestionWithAnswer);
    const questionProps = {
      question:selectedQuestionWithAnswer,
      answer:responseDetailAnswer,
      surveyResponseId:surveyResponseId,
      surveyResponseDetailId:selectedResponseDetail.id,
      disabled:true,
      size:'small',
      onError:() => {},
      onWarning:() => {},
      onAnswerChange:() => {}
    };

    if(selectedQuestionWithAnswer.type === 'condition' || selectedQuestionWithAnswer.type === 'multi_condition'){
      return (
        <QuestionCell {...questionProps}
                      getConditions={() => Promise.resolve('')}
        />
      );
    }

    if(selectedQuestionWithAnswer.type === 'dynamic_list'){
      return (
        <QuestionCell {...questionProps}
                      getDynamicListItems={() => Promise.resolve('')}
        />
      );
    }

    if(selectedQuestionWithAnswer.type === 'survey'){
      return (
        <div className="survey-response-modal-placeholder">
          <Translation>{(t, {i18n}) => t('SURVEY_RESPONSE_DETAIL_UNAVAILABLE')}</Translation>
        </div>
      );
    }

    return <QuestionCell {...questionProps} />;
  };

  const metadataRows = [
    {label:'SURVEY_RESPONSE_ID', value:surveyResponseId},
    {label:'CREATED', value:surveyResponseForMeta && surveyResponseForMeta.created_at ? momentFromDateTime(surveyResponseForMeta.created_at) : ''},
    {label:'COMPLETED_ON', value:surveyResponseForMeta && surveyResponseForMeta.completed_at ? momentFromDateTime(surveyResponseForMeta.completed_at) : ''},
    {label:'UPDATED_DATE', value:surveyResponseForMeta && surveyResponseForMeta.updated_at ? momentFromDateTime(surveyResponseForMeta.updated_at) : ''},
    {label:'LOCK_STATE', value:surveyResponseForMeta && surveyResponseForMeta.lock_state ? surveyResponseForMeta.lock_state : lockedState}
  ];

  const questionLabel = selectedQuestionWithAnswer && (selectedQuestionWithAnswer.unparsed_label || selectedQuestionWithAnswer.label || selectedQuestionWithAnswer.key);
  const shouldShowQuestionLabelToggle = typeof questionLabel === 'string' && questionLabel.length > 111;

  const modalDescription = !selectedQuestion ?
    i18next.t('SELECT_QUESTION_TO_VIEW_DETAILS')
    : !selectedResponseDetail && !responseDetailLoading ?
      i18next.t('SELECT_SURVEY_RESPONSE_DETAIL')
      :
      i18next.t('SURVEY_RESPONSE_MODAL_DESCRIPTION');
  const rightColumnTabs = [{key:'HISTORY'}, {key:'DATA_QUERIES'}];
  const canDeleteRestore = ['admin', 'admin_level_3', 'admin_level_4'].includes(roleForCompany(company));
  const isRestoreAction = selectedObjectIsDeleted;
  const confirmationTitleKey = selectedResponseDetail
    ? (isRestoreAction ? 'RESTORE_SURVEY_RESPONSE_DETAIL' : 'DELETE_SURVEY_RESPONSE_DETAIL')
    : (isRestoreAction ? 'RESTORE_SURVEY_RESPONSE' : 'DELETE_SURVEY_RESPONSE');
  const confirmationDescriptionKey = selectedResponseDetail
    ? (isRestoreAction ? 'RESTORE_SURVEY_RESPONSE_DETAIL_CONFIRMATION' : 'DELETE_SURVEY_RESPONSE_DETAIL_CONFIRMATION')
    : (isRestoreAction ? 'RESTORE_SURVEY_RESPONSE_CONFIRMATION' : 'DELETE_SURVEY_RESPONSE_CONFIRMATION');

  return (
    <GeneralModal title={modalTitle}
                  description={modalDescription}
                  width={'calc(100vw - 30px)'}
                  height={'calc(100vh - 30px)'}
                  isOpen={open}
                  maxWidth={1400}
                  maxHeight={900}
                  showFooter={false}
                  showHeader={true}
                  noTitleHeading={true}
                  showSaveButton={false}
                  handleCloseModal={close}
                  presentedInsideParentModal={true}
                  containsDropDown={true}
                  clickOutsideToClose={true}>
      <div className="survey-response-modal-layout">
        <div className="survey-response-modal-column survey-response-modal-left-column">
          <div className="survey-response-modal-section">
            <div className="survey-response-modal-scrollable-content">
              <div className="survey-response-modal-selectors">
              <LabeledDropDown className="survey-response-modal-question-picker"
                               label={i18next.t('QUESTION')}
                               value={selectedQuestion ? selectedQuestion.value : null}
                               handleSave={handleQuestionChange}
                               isClearable={true}
                               disabled={!resolvedSurveyReference || !fetchQuestionsForSurveyByReference}
                               fetchObjects={fetchQuestionObjects}
                               optionsArrayFormatter={questionOptionsFormatter}
                               labelPropertiesArray={['label']}
                               valuePropertiesArray={['key']}
                               responseTotalKeysArray={['data', 'data', 'survey_questions_for_survey_version_collection']}
                               responseObjectKeysArray={['data', 'data', 'survey_questions_for_survey_version_collection']}
                               saveWithOptionsKey={true}
                               doNotLoadOnMenuBottom={true}
                               notifyFetchFailed={notifyQuestionFetchFailed}
                               placeholder={resolvedSurveyReference ? i18next.t('SEARCH_QUESTION') : i18next.t('QUESTION')}
              />

              {selectedQuestion ?
                <LabeledDropDown className="survey-response-modal-detail-picker"
                                 label={i18next.t('SURVEY_RESPONSE_DETAIL')}
                                 value={selectedResponseDetail ? selectedResponseDetail.id : null}
                                 handleSave={handleResponseDetailChange}
                                 isClearable={true}
                                 options={responseDetailOptions}
                                 disabled={responseDetailLoading || responseDetailOptions.length === 0}
                                 placeholder={responseDetailLoading ? i18next.t('LOADING') : i18next.t('SELECT_ELLIPSIS')}
                />
                :
                null
              }
              </div>

            {selectedQuestion && canDeleteRestore ?
              <LabeledSwitch className="survey-response-modal-include-deleted"
                             label={i18next.t('INCLUDE_DELETED')}
                             checked={includeDeleted}
                             onChange={(aChecked) => setIncludeDeleted(aChecked)}
                             showCheckedLabels={false}
              />
              :
              null
            }

            <Separator margin={'10px'} />

            {questionLoadError && questionLoadError.length > 0 ?
              <div className="survey-response-modal-placeholder">
                {questionLoadError}
              </div>
              :
              null
            }

            {!selectedResponseDetail ?
              <>
                <div className="survey-response-modal-meta-grid">
                  {metadataRows.map((row) => (
                    <div className="survey-response-modal-meta-row" key={row.label}>
                      <div className="survey-response-modal-meta-label">
                        <Translation>{(t, {i18n}) => t(row.label)}</Translation>
                      </div>
                      
                      <div className="survey-response-modal-meta-value">
                        {row.value && `${row.value}`.length > 0 ? row.value : '-'}
                      </div>
                    </div>
                  ))}
                </div>
              </>
              :
              <>
                {responseDetailLoading ?
                  <div className="survey-response-modal-placeholder">
                    <Translation>{(t, {i18n}) => t('LOADING')}</Translation>
                  </div>
                  :
                  selectedResponseDetail ?
                  <>
                    <div className="survey-title">
                      <div className={showFullQuestionLabel ? '' : 'survey-response-modal-question-label-collapsed'}>
                        {typeof questionLabel === 'string' ? parse(questionLabel) : questionLabel}
                      </div>
                    </div>

                    {shouldShowQuestionLabelToggle ?
                      <div className="survey-response-modal-question-label-toggle"
                           onClick={() => setShowFullQuestionLabel(!showFullQuestionLabel)}>
                        {i18next.t(showFullQuestionLabel ? 'SHOW_LESS' : 'SHOW_MORE')}
                      </div>
                      :
                      null
                    }

                    {renderQuestionCell()}

                    {selectedResponseDetail && (createDataQueryNote || addDataQuery) ?
                      <div className="survey-response-modal-add-data-query-button"
                           onClick={createDataQueryNote ? openDataQueryModal : () => addDataQuery(selectedResponseDetail.id, surveyResponseId)}>
                        <AddCircleOutlineIcon style={{height:'20px', width:'20px', color:'#2D81C9', marginRight:'5px'}}/>
                        <Translation>{(t, {i18n}) => t('ADD_A_DATA_QUERY')}</Translation>
                      </div>
                      :
                      null
                    }

                    <div className="survey-response-modal-meta-grid">
                      <div className="survey-response-modal-meta-row">
                        <div className="survey-response-modal-meta-label">
                          <Translation>{(t, {i18n}) => t('SURVEY_RESPONSE_DETAIL_ID')}</Translation>
                        </div>
                        <div className="survey-response-modal-meta-value">
                          {selectedResponseDetail ? selectedResponseDetail.id : '-'}
                        </div>
                      </div>
                      <div className="survey-response-modal-meta-row">
                        <div className="survey-response-modal-meta-label">
                          <Translation>{(t, {i18n}) => t('CREATED')}</Translation>
                        </div>
                        <div className="survey-response-modal-meta-value">
                          {selectedResponseDetail && selectedResponseDetail.created_at ? momentFromDateTime(selectedResponseDetail.created_at) : '-'}
                        </div>
                      </div>
                      {selectedResponseDetail.deleted_at ?
                        <div className="survey-response-modal-meta-row">
                          <div className="survey-response-modal-meta-label">
                            <Translation>{(t, {i18n}) => t('DELETED_AT')}</Translation>
                          </div>
                          <div className="survey-response-modal-meta-value">
                            {momentFromDateTime(selectedResponseDetail.deleted_at)}
                          </div>
                        </div>
                        :
                        null
                      }
                    </div>
                  </>
                  :
                  null
                }
              </>
            }

            </div>

            {canDeleteRestore ?
            <div className="survey-response-modal-delete-button">
              {selectedObjectIsDeleted ?
                <BlueButton name={i18next.t(selectedResponseDetail ? 'RESTORE_SURVEY_RESPONSE_DETAIL' : 'RESTORE_SURVEY_RESPONSE')}
                            onClick={() => setDeleteRestoreConfirmationOpen(true)}
                            loading={saving}
                />
                :
                <RedButton name={i18next.t(selectedResponseDetail ? 'DELETE_SURVEY_RESPONSE_DETAIL' : 'DELETE_SURVEY_RESPONSE')}
                           onClick={() => setDeleteRestoreConfirmationOpen(true)}
                           loading={saving}
                />
              }
            </div>
            : null
            }
          </div>
        </div>

        <div className="survey-response-modal-column survey-response-modal-right-column">
          <TabBar className="survey-response-modal-right-tabs"
                  tabBarTabsArray={rightColumnTabs}
                  initialSelectedTabKey={rightTabKey}
                  selectedTabKeyChanged={(aTabKey) => setRightTabKey(aTabKey)}
          />

          <div className="survey-response-modal-right-tab-content">
            {rightTabKey === 'DATA_QUERIES' ?
              <DataQueriesList key={'survey-response-data-queries-' + (surveyResponse && surveyResponse.id ? surveyResponse.id : 'survey-response') + '-' + (selectedResponseDetail && selectedResponseDetail.id ? selectedResponseDetail.id : '') + '-' + dataQueryListKey}
                               company={company}
                               patient={patient}
                               height={'calc(100vh - 240px)'}
                               presentedInsideParentModal={true}
                               surveyResponseId={surveyResponse && surveyResponse.id}
                               surveyResponseDetailId={selectedResponseDetail && selectedResponseDetail.id}
                               showAddDataQuery={false}
                               getDataQueryNotes={getDataQueryNotes}
                               getDataQueryNoteComments={getDataQueryNoteComments}
                               resolveDataQueryNote={resolveDataQueryNote}
                               createDataQueryComment={createDataQueryComment}
                               showQuestionForDataQuery={() => {}}
              />
              :
              <PaperTrailVersionsList key={(selectedResponseDetail && selectedResponseDetail.id ? selectedResponseDetail.id : surveyResponseId) + '-' + responseDetailsReloadKey}
                                      company={company}
                                      userId={null}
                                      type={selectedResponseDetail && selectedResponseDetail.id ? 'SurveyResponseDetail' : 'SurveyResponse'}
                                      id={selectedResponseDetail && selectedResponseDetail.id ? selectedResponseDetail.id : surveyResponseId}
                                      getAdminHistories={getAdminHistories}
                                      studyClosed={lockedState === true || lockedState === 'locked'}
                                      showPatientSearch={showPatientSearch}
                                      initialFromDate={resolvedInitialFromDate || initialFromDate}
                                      initialToDate={initialToDate}
              />
            }
          </div>
        </div>
      </div>
      
      <ConfirmationModal title={i18next.t(confirmationTitleKey)}
                         description={i18next.t(confirmationDescriptionKey)}
                         isOpen={deleteRestoreConfirmationOpen}
                         reject={() => setDeleteRestoreConfirmationOpen(false)}
                         confirm={selectedResponseDetail ? surveyResponseDetailDeleteOrRestore : surveyResponseDeleteOrRestore}
                         loading={saving}
                         presentedInsideParentModal={true}
      />
      
      <AddDataQueryModal open={dataQueryModalOpen}
                         close={() => setDataQueryModalOpen(false)}
                         patient={patient}
                         user={user}
                         surveyResponseDetail={selectedResponseDetail}
                         surveyResponse={surveyResponse}
                         createDataQueryNote={createDataQueryNote}
                         onCreate={() => setDataQueryListKey((aKey) => aKey + 1)}
                         presentedInsideParentModal={true}
      />
    </GeneralModal>
  );
};

export default SurveyResponseModal;
